import { useCallback, useState } from "react";
import { updateSmoothingRate } from "../../../api/smoothing";
import useTestId from "../../../hooks/useTestId";
import { GraphSystems, TestSearchResult } from "../../../types";
import SmoothingSlider from "../../SmoothingSlider";
import BottomBackToMain from "../BottomBackToMain";
import GraphsGrid from "../GraphsGrid";
import QuickView from "../QuickView";
import HistoricDataResultsPopup from "./HistoricDataResultsPopup";
import PhysiologicalSystemButton from "./PhysiologicalSystemButton";
import { useGraphs } from "../../../context/GraphsProvider";
import { useAuth } from "../../../context/AuthContext";
import { useGraphEnlarge } from "../../../context/GraphEnlargeContext";
import RadioButton from "../../RadioButton";

const GraphSystemsConfig: { id: GraphSystems; label: string }[] = [
    { id: GraphSystems.METABOLIC, label: "Metabolic" },
    { id: GraphSystems.CARDIAC, label: "Cardiac" },
    { id: GraphSystems.VENTILATORY, label: "Ventilatory" },
    { id: GraphSystems.GAS_EXCHANGE, label: "Gas Exchange" },
    { id: GraphSystems.VENTILATORY_EQUIVALENTS, label: "Ventilatory Equiv." },
];

export default function GraphsGridPageLayout({
    title,
    systemIdToShow,
    setSystemIdToShow,
    historicTestsToShow,
    setHistoricTestsToShow,
    smoothingValue,
    setSmoothingValue,
}: {
    title: string;
    systemIdToShow: GraphSystems | undefined;
    setSystemIdToShow: (value: GraphSystems | undefined) => void;
    historicTestsToShow: number[];
    setHistoricTestsToShow: (value: number[]) => void;
    smoothingValue: number;
    setSmoothingValue: (value: number) => void;
}) {
    const { setGraphIdToEnlarge } = useGraphEnlarge();
    const [isHistoricPopupOpen, setIsHistoricPopupOpen] = useState(false);
    const [historicData, setHistoricData] = useState<TestSearchResult[]>([]);
    const { graphs, graphsType, loading, mutate } = useGraphs();
    const { isAllowed } = useAuth();

    const formatDate = (date: Date) => {
        return `${date.toLocaleString("en-US", { month: "long" })} ${date
            .getDate()
            .toString()
            .padStart(2, "0")}, ${date.getFullYear()}`;
    };

    const renderPreviousTestsHeader = () => {
        if (!historicTestsToShow.length || !graphs?.length || loading)
            return null;

        const source = graphs[0];
        const dataToShow = historicData.filter((item) =>
            historicTestsToShow.includes(item.testId)
        );
        return (
            <div className="absolute flex gap-[28px] left-[50%] translate-x-[-50%]">
                <span className="text-gray-600">Previous Tests:</span>
                {dataToShow.map((item) => {
                    return (
                        <div className="flex items-center" key={item.testId}>
                            <div
                                style={{
                                    width: "24px",
                                    height: "24px",
                                    marginRight: "12px",
                                    backgroundColor:
                                        source[
                                            `response1_xy_${item.testId}_color`
                                        ],
                                }}
                            />
                            <p className="capitalize">
                                {formatDate(item.testDate)}
                            </p>
                        </div>
                    );
                })}
            </div>
        );
    };

    const testId = useTestId();

    const onSliderMouseRelease = useCallback(async () => {
        const payload =
            graphsType === "inbar-12"
                ? { inbar12: smoothingValue }
                : { wasserman9: smoothingValue };
        await updateSmoothingRate({ testId, ...payload });
        mutate();
    }, [smoothingValue]);

    return (
        <>
            <HistoricDataResultsPopup
                isOpen={isHistoricPopupOpen}
                setIsOpen={setIsHistoricPopupOpen}
                setHistoricData={setHistoricData}
                onConfirm={(ids) => {
                    setHistoricTestsToShow(ids);
                    mutate();
                }}
            />
            <div className="flex xl:pl-5 gap-6 pb-bottomPanel">
                {/* MARK: left sidebar
                 */}
                <div className="flex flex-col gap-2 xl:gap-3.5 h-full w-leftSidebar shrink-0">
                    <QuickView />
                    <SmoothingSlider
                        value={smoothingValue}
                        setValue={setSmoothingValue}
                        onMouseRealease={onSliderMouseRelease}
                        disabled={
                            !isAllowed(
                                graphsType === "inbar-12"
                                    ? "Inbar12 Smoothing"
                                    : graphsType === "wasserman-9"
                                    ? "Wasserman9 Smoothing"
                                    : "Anaerobic smoothing"
                            )
                        }
                    />
                    <div className="w-full h-[1px] bg-[#999999]" />

                    <div className="flex flex-col gap-3 xl:gap-4">
                        <div className="flex flex-col gap-2 xl:gap-4">
                            <h5 className="font-semibold my-1 xl:my-2 leading-tight text-sm xl:text-base">
                                Physiological Systems
                            </h5>
                            {GraphSystemsConfig.map(
                                ({ id: systemId, label }) => {
                                    const active = systemIdToShow === systemId;
                                    return (
                                        <PhysiologicalSystemButton
                                            key={systemId}
                                            label={label}
                                            value={systemId}
                                            active={active}
                                            onClick={(value) => {
                                                setGraphIdToEnlarge(null);
                                                setSystemIdToShow(
                                                    active
                                                        ? undefined
                                                        : (value as GraphSystems)
                                                );
                                            }}
                                        />
                                    );
                                }
                            )}
                        </div>
                        {graphsType === "inbar-12" && (
                            <>
                                <div className="w-full h-[1px] bg-[#999999]" />
                                <div
                                    onClick={() => {
                                        if (historicTestsToShow.length) {
                                            setHistoricTestsToShow([]);
                                            mutate();
                                        } else {
                                            setIsHistoricPopupOpen(true);
                                        }
                                    }}
                                    className="flex text-start gap-1.5 xl:gap-3 text-[13px] xl:text-base"
                                >
                                    <RadioButton
                                        isSquared
                                        checked={
                                            isHistoricPopupOpen ||
                                            Boolean(historicTestsToShow.length)
                                        }
                                        onChange={() => {}}
                                    />
                                    <span className="font-manrope font-semibold text-black">
                                        Previous Tests Display
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {/* MARK: Main content
                 */}
                <div className="flex flex-col gap-10 h-full min-w-0 w-full">
                    <div className="flex flex-col gap-6 h-full w-full">
                        <div className="flex flex-col gap-0 xl:gap-2 w-full">
                            <div
                                className={`relative flex w-full items-center`}
                            >
                                <h1 className="text-blue-dark font-bold text-base xl:text-[22px] leading-9">
                                    {title}
                                </h1>
                                {renderPreviousTestsHeader()}
                            </div>
                            <GraphsGrid />
                        </div>
                    </div>
                    <BottomBackToMain isFixed />
                </div>
            </div>
        </>
    );
}
